import { useState } from 'react';
import styles from '../styles/LandingPage.module.css';
import { logEmailSubmission, logEvent } from '../utils/analytics';
import { useBurgerDisplay } from '../hooks/useBurgerDisplay';
import { useLandingEmailCapture } from '../hooks/useLandingEmailCapture';

export const LandingPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailStarted, setEmailStarted] = useState(false);
  const burgerHidden = useBurgerDisplay();
  const captureEmail = useLandingEmailCapture();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = window.location.href;
      const response = await fetch('https://n8n.inkifylabs.com/webhook/2eeada4c-f8b1-4261-b430-8b1418a2ea24', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, url }),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      logEmailSubmission(true);
      logEvent(
        'Email',
        'Submit',
        undefined,
        undefined,
        'email_submit',
        undefined,
        { email, url, status: 'success', page: 'LandingPage' }
      );
      setEmail('');
    } catch (error) {
      console.error('Error submitting email:', error);
      logEmailSubmission(false);
      logEvent(
        'Email',
        'Submit',
        undefined,
        undefined,
        'email_submit',
        undefined,
        { email, url: window.location.href, status: 'fail', page: 'LandingPage' }
      );
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>STA</div>
        {!burgerHidden && (
          <button
            className={styles.burger}
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <span className={styles.burgerIcon}>
              {/* Simple burger icon SVG */}
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect y="5" width="28" height="3" rx="1.5" fill="#fff"/>
                <rect y="12.5" width="28" height="3" rx="1.5" fill="#fff"/>
                <rect y="20" width="28" height="3" rx="1.5" fill="#fff"/>
              </svg>
            </span>
          </button>
        )}
        <nav className={`${styles.nav} ${menuOpen ? styles.navOpen : ''}`}>
          <a href="#" className={styles.navLink}>Listen Now</a>
          <a href="#" className={styles.navLink}>Join the Community</a>
          <a href="#" className={styles.navLink}>Membership</a>
          <a href="#" className={styles.navLink}>Shop</a>
          <a href="#" className={styles.navLink}>Contact Us</a>
        </nav>
      </header>

      <main className={styles.mainContent}>
        <section className={styles.leftSection}>
          <h1 className={styles.title}>Listen Now To STA Stories</h1>
          {/* Mobile-only: Show stacked images and CTA */}
          <div className={styles.mobileShowcase}>
            <img src="/sta-stories-podcast-cover.jpg" alt="STA Stories" className={styles.mobileShowImage} />
            <div className={styles.podcastCtaContainer}>

              <div className={styles.podcastButtons}>
                <a
                  href="https://open.spotify.com/show/4MLBJdxjEDlkJbaS3HXjPc?si=80eb9f16c035473e"
                  className={styles.podcastButton}
                  aria-label="Listen on Spotify"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => logEvent(
                    'Podcast Player',
                    'Click',
                    'Spotify',
                    undefined,
                    'podcast_player_click',
                    'spotify',
                    { page: 'LandingPage' }
                  )}
                >
                  <img src="/spotify-icon.svg" alt="Spotify" />
                </a>
                <a
                  href="https://podcasts.apple.com/us/podcast/story-time-animated/id1673125294"
                  className={styles.podcastButton}
                  aria-label="Listen on Apple Podcasts"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => logEvent(
                    'Podcast Player',
                    'Click',
                    'Apple Podcasts',
                    undefined,
                    'podcast_player_click',
                    'apple',
                    { page: 'LandingPage' }
                  )}
                >
                  <img src="/apple-podcast.svg" alt="Apple Podcasts" />
                </a>
                <a
                  href="https://music.amazon.com/podcasts/bdff5bef-c854-4537-a5d8-2c34a83480c4/story-time-animated"
                  className={styles.podcastButton}
                  aria-label="Listen on Amazon Music"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => logEvent(
                    'Podcast Player',
                    'Click',
                    'Amazon Music',
                    undefined,
                    'podcast_player_click',
                    'amazon',
                    { page: 'LandingPage' }
                  )}
                >
                  <img src="/amazon-music.svg" alt="Amazon Music" />
                </a>
              </div>
            </div>
          </div>
          {captureEmail && (
            <>
              <h2>Can’t Stop Thinking About That Story?</h2>
              <p className={styles.description}>
                Binge powerful stories, discover twists you never saw coming, and get early access to what’s next. All without a single interruption.
              </p>
              <form onSubmit={handleSubmit} className={styles.form}>
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (!emailStarted) {
                      setEmailStarted(true);
                      logEvent(
                        'Email',
                        'Start Enter',
                        undefined,
                        undefined,
                        'email_enter',
                        undefined,
                        { page: 'LandingPage' }
                      );
                    }
                  }}
                  required
                  className={styles.input}
                />
                <button type="submit" className={styles.button} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Claim My 7-Day Trial'}
                </button>
              </form>
            </>
          )}
          <div className={styles.followUsSection}>
            <h3 className={styles.followUsTitle}>Follow Us</h3>
            <div className={styles.socialLinks}>
              <a
                href="https://www.youtube.com/@StoryTimeAnimated"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logEvent(
                  'Follow Us',
                  'Click',
                  'YouTube',
                  undefined,
                  'follow_us_click',
                  'youtube',
                  { page: 'LandingPage' }
                )}
              >
                <img src="/youtube.svg" alt="YouTube" className={styles.socialIcon} />
              </a>
              <a
                href="https://www.tiktok.com/@storytimeanimated__"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logEvent(
                  'Follow Us',
                  'Click',
                  'TikTok',
                  undefined,
                  'follow_us_click',
                  'tiktok',
                  { page: 'LandingPage' }
                )}
              >
                <img src="/tiktok.svg" alt="TikTok" className={styles.socialIcon} />
              </a>
              <a
                href="https://www.instagram.com/storytimeanimatedyt"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logEvent(
                  'Follow Us',
                  'Click',
                  'Instagram',
                  undefined,
                  'follow_us_click',
                  'instagram',
                  { page: 'LandingPage' }
                )}
              >
                <img src="/instagram.svg" alt="Instagram" className={styles.socialIcon} />
              </a>
              <a
                href="https://www.facebook.com/StoryTimeAnimatedOfficial"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logEvent(
                  'Follow Us',
                  'Click',
                  'Facebook',
                  undefined,
                  'follow_us_click',
                  'facebook',
                  { page: 'LandingPage' }
                )}
              >
                <img src="/facebook.svg" alt="Facebook" className={styles.socialIcon} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerLeft}>STA</div>
        <div className={styles.footerCenter}>©Copyright. All rights reserved</div>
        <div className={styles.footerRight}>
          <span>hello@inkifylabs.com</span>
        </div>
      </footer>
    </div>
  );
};
